import {
  Box,
  Skeleton,
  useToast,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import { ErrorBoundary } from '@sentry/react';
import React from 'react';
import { useNavigate } from 'react-router';

import { useGetUserQuery } from '../../../types/dashboard-core-gqlTypes.generated.d';
import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { ExploDashboardContainer } from 'packages/explo/ui/ExploDashboardContainer';
import { ExploReportBuilderContainer } from 'packages/explo/ui/ExploReportBuilderContainer';
import { StratsTemplate } from 'packages/strats/ui/StratsTemplate';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

// Exports
export const StratsRoute = () => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const navigate = useNavigate();
  const { creditFacilityId } = useCreditFacilityContext();
  const { data: userData, loading: userLoading } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  if (userLoading) {
    return <Skeleton height="600px" data-testid="strats-route-loading" />;
  }

  if (!userData || !userData.user) {
    // TODO: currently defaulting similar app behavior when missing user data
    // Manage this potential error case
    return <Skeleton height="600px" data-testid="strats-route-missing-user" />;
  }

  if (!flags) {
    return <Skeleton height="600px" data-testid="strats-route-flag-unset" />;
  }

  if (
    flags.productsStratsPermissions !== 'allow' &&
    !flags.showExploReportBuilder
  ) {
    navigate('/');
    return <Skeleton height="600px" data-testid="strats-route-reroute" />;
  }

  const organizationId = userData.user.organizationId;

  // Use this variable for errors that bubble up to the component level route
  // This ensures we only toast the error once when there are multiple errors
  // We should use Chakra's toast.isActive, or a react hook in general, but the state re-renders when there are multiple errors in a given error boundary
  let routeLevelError = false;
  return (
    <Box className="main" width="100%">
      <ErrorBoundary
        onError={(err) => {
          if (!routeLevelError && !toast.isActive(errorToastId)) {
            errorToastConfig.title = `Failed to load strats page ${err}`;
            routeLevelError = true;
            toast(errorToastConfig);
            navigate('/');
          }
        }}
      >
        <StratsTemplate header="Strats">
          <Tabs mb="3" variant="line">
            <TabList>
              {flags.productsStratsPermissions === 'allow' && (
                <Tab>Dashboard</Tab>
              )}
              {flags.showExploReportBuilder && <Tab>Report Builder</Tab>}
            </TabList>

            <TabPanels>
              {/*
               * TODO: at the moment, we're still in this intermediary state
               * where we're supporting both Quicksights & Explo, thus requiring
               * all these flags and respective states. Ideally we'll only support
               * one platform soon and can clean up below to not be so conditional
               */}

              {flags.productsStratsPermissions === 'allow' && (
                <TabPanel>
                  <ExploDashboardContainer
                    organizationId={organizationId}
                    creditFacilityId={creditFacilityId}
                    exploPlacement="borrowerStratsPage"
                  />
                </TabPanel>
              )}

              {flags.showExploReportBuilder && (
                <TabPanel>
                  <ExploReportBuilderContainer
                    organizationId={organizationId}
                    creditFacilityId={creditFacilityId}
                    exploPlacement="borrowerStratsPage"
                  />
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </StratsTemplate>
      </ErrorBoundary>
    </Box>
  );
};
